import UploadHubModalAdd from 'components/UploadHub/UploadHubModalAdd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUploadFiles from 'api/hooks/useUploadFiles';
import { SkeletonContent } from 'components/WritingAssistant/ui/MyContent';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import UploadHubItem from 'components/UploadHub/UploadHubItem';
import UploadHubSearchBar from 'components/UploadHub/UploadHubSearchBar';
import UploadHubFilters from 'components/UploadHub/UploadHubFilters';
import {
  fileMatchesUploadHubFilters,
  parseUploadHubFiltersFromHash,
  uploadHubFiltersToHash,
  type UploadHubFiltersState,
} from 'components/UploadHub/UploadHubFilters/uploadHubFiltersUtils';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import Loading from 'components/Login/Loading';
import CustomScrollbar from 'components/Common/CustomScrollbar';

const UploadHub = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [files, setFiles] = useState<UploadHubListItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<UploadHubFiltersState>(() =>
    parseUploadHubFiltersFromHash(window.location.hash)
  );
  const { getUploadHubList } = useUploadFiles();
  const { addMetaTags } = useAddMetaTags();
  const navigate = useNavigate();

  const handleApplyFilters = (newFilters: UploadHubFiltersState) => {
    setFilters(newFilters);
    navigate({ hash: uploadHubFiltersToHash(newFilters) }, { replace: true });
  };

  const visibleFiles = files.filter(
    (file) =>
      (searchValue === '' || file.fileName.toLowerCase().includes(searchValue.toLowerCase())) &&
      fileMatchesUploadHubFilters(file, filters)
  );

  const fetchFiles = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  fetchFiles.current = async (signal) => {
    try {
      const results = await getUploadHubList({ signal });
      if (results) {
        setFiles(results);
        const title = `Your files - PolicyMogul`;
        addMetaTags({ title, hash: location.hash });
        setIsLoading(false);
      }
    } catch {
      //Already use toaster
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchFiles.current?.(controller.signal);
    return () => controller.abort('Files list cancelled.');
  }, []);

  return (
    <>
      <div className={`bg-white monitor-items-bar`}>
        <div className='mx-auto main-content-wrapper flex-centered justify-content-between resize-elements-container'>
          <div className='side-left-element pl-md-5 pl-3 d-none d-lg-inline-block'>
            <p className='mb-0 title-h4 nowrap-item monitor-dropdown-item'>Files</p>
          </div>
          <div className='main-content flex-centered position-relative filters-general-container justify-content-between pr-xl-5 px-md-4 px-3'>
            <h4 className='title-h4-bold mb-0'>{"My team's files"}</h4>
            <div className='d-flex align-items-center gap-15'>
              <UploadHubSearchBar value={searchValue} onChange={setSearchValue} />
              <UploadHubFilters files={files} filters={filters} onApply={handleApplyFilters} />
              <button
                className='general-button action-button specific-x-padding-15 rounded-pill py-1'
                onClick={() => setIsOpen(true)}
              >
                <span className='icon-upload specific-left-margin-xl-3 specific-right-margin-xl-10 d-inline-block-centered paragraph-p4'></span>
                <span className='d-xl-inline-block d-none'>Upload a new file</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='flex-grow-1'>
        <CustomScrollbar className={'simple-scrollbar'} maximalThumbYSize={100}>
          <div className={`h-100 row mx-auto main-content-wrapper align-items-start specific-top-padding-32`}>
            <div className='w-100 px-xl-5 px-md-4 px-3 h-100 mx-auto dashboard-root-container'>
              <div className='box-top-rounded p-lg-5 p-3 flex-grow-1 '>
                {isLoading ? (
                  <SkeletonContent />
                ) : visibleFiles.length > 0 ? (
                  <ul className='policy-list px-0 items-list upload-hub-list'>
                    {visibleFiles.map((file) => (
                      <UploadHubItem key={`item-${file.id}`} file={file} />
                    ))}
                  </ul>
                ) : (
                  <p className='text-center mt-5 pt-5 title-h5-m main-light-text px-5 mt-5 pt-lg-5'>
                    {files.length > 0 ? 'No files match your search or filters' : 'Files you upload will appear here'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </CustomScrollbar>
      </div>
      <UploadHubModalAdd filesState={{ listFiles: files, setListFiles: setFiles }} openState={{ isOpen, setIsOpen }} />
    </>
  );
};

export default withAuthenticationRequired(UploadHub, {
  onRedirecting: () => <Loading />,
});
